(ns frontend.view.nev.kyc.process.generic-person-list-editor
  (:require
   [clojure.data :as data]
   [common.localization :as loc]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [frontend.state.current-session :as session]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.dynamic.view :as dynamic-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.kyc.process.editor-utils :as editor-utils]
   [frontend.view.nev.kyc.process-status-views.view-utils :as view-utils]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.tooltip.view :as tooltip]))

(defn get-person-display-name
  "Get display name for a person based on available fields"
  [person person-type]
  (case person-type
    :substantial-us-owner
    (:kyc-substantial-us-owner/full-name person)

    :controlling-person
    (str (:kyc-controlling-person/first-name person)
         " "
         (:kyc-controlling-person/last-name person))

    "Unknown"))

(defn get-person-summary-fields
  "Get the summary fields to display in the table for different person types"
  [person person-type schema lang-kw]
  (case person-type
    :substantial-us-owner
    [[:name (:kyc-substantial-us-owner/full-name person)]
     [:address (:kyc-substantial-us-owner/street-address person)]
     [:country (when-let [country (:kyc-substantial-us-owner/country person)]
                 (kyc-utils/localize-display-string schema :kyc-substantial-us-owner/country country lang-kw))]]

    :controlling-person
    [[:first-name (:kyc-controlling-person/first-name person)]
     [:last-name (:kyc-controlling-person/last-name person)]
     [:country (when-let [country (:kyc-controlling-person/country person)]
                 (kyc-utils/localize-display-string schema :kyc-controlling-person/country country lang-kw))]
     [:control-type (when-let [control-type (:kyc-controlling-person/type-of-control person)]
                      (kyc-utils/localize-display-string schema :kyc-controlling-person/type-of-control control-type lang-kw))]]

    []))

(defn get-table-headers
  "Get table headers for different person types"
  [person-type schema lang-kw]
  (case person-type
    :substantial-us-owner
    [""
     (kyc-utils/localize-question schema :kyc-substantial-us-owner/full-name lang-kw)
     (kyc-utils/localize-question schema :kyc-substantial-us-owner/street-address lang-kw)
     (kyc-utils/localize-question schema :kyc-substantial-us-owner/country lang-kw)
     ""]

    :controlling-person
    [""
     (kyc-utils/localize-question schema :kyc-controlling-person/first-name lang-kw)
     (kyc-utils/localize-question schema :kyc-controlling-person/last-name lang-kw)
     (kyc-utils/localize-question schema :kyc-controlling-person/country lang-kw)
     (kyc-utils/localize-question schema :kyc-controlling-person/type-of-control lang-kw)
     ""]

    []))

(defn open-person-editor
  [{:keys [idx existing-person kyc-entity-editor process-id state-atom lang-kw question-attribute
           question-meta answers-atom mandatory? read-only? save! person-type]}]
  (let [kyc-process (session/get-entity process-id)
        person-answers (r/atom (or existing-person {}))
        field-errors-atom (r/atom {})
        persons (get @answers-atom question-attribute)
        person-template-schema (:data/template question-meta)
        calc-new-person-answer (fn []
                                 (if (>= idx (count persons))
                                   (concat persons
                                           [(assoc @person-answers
                                                   :ord
                                                   (count persons))])
                                   (->> persons
                                        (map-indexed (fn [uidx person]
                                                       (assoc (if (= uidx idx)
                                                                @person-answers
                                                                person)
                                                              :ord
                                                              uidx)))
                                        (vec))))]
    (modals/open-modal!
     dynamic-modal/modal-key
     {:title               (case person-type
                             :substantial-us-owner (loc/tr :add-substantial-us-owner nil lang-kw)
                             :controlling-person (loc/tr :add-controlling-person nil lang-kw)
                             (loc/tr :add-person nil lang-kw))
      :render-fn           (fn [_state]
                             [:div
                              [kyc-entity-editor
                               {:container-id    (str (name person-type) "-" idx)
                                :process-id      process-id
                                :state-atom      state-atom
                                :save!           identity
                                :mandatory?      mandatory?
                                :skip-info?      true
                                :read-only?      read-only?
                                :answers-atom    person-answers
                                :field-errors-atom field-errors-atom
                                :template-schema person-template-schema
                                :lang-kw         lang-kw}]])
      :show-button?        (not read-only?)
      :button-text         (loc/tr :save nil lang-kw)
      :custom-validator-fn (fn [_state]
                             (let [[old-only new-only _] (data/diff existing-person @person-answers)
                                   changes? (or (seq old-only)
                                                (seq new-only))]
                               (view-utils/modal-validator-tooltip
                                {:lang-kw           lang-kw
                                 :template-schema   person-template-schema
                                 :answers-atom      person-answers
                                 :field-errors-atom field-errors-atom
                                 :kyc-process       kyc-process
                                 :changes?          changes?})))
      :on-submit!          (fn [_]
                             (let [new-value (calc-new-person-answer)]
                               (swap! answers-atom assoc question-attribute new-value)
                               (save!)))
      :on-before-close!    (fn [close!]
                             (if (= (get @answers-atom question-attribute) (calc-new-person-answer))
                               (close!)
                               (modals/open-modal!
                                confirmation-modal/modal-key
                                {:body           (loc/tr :confirm-close-kyc-modal-without-saving nil lang-kw)
                                 :confirm-text   (loc/tr :close-without-saving nil lang-kw)
                                 :confirm-icon   ""
                                 :skip-checkbox? true
                                 :on-confirm     #(close!)})))})))

(defn view
  [{:keys [_kyc-entity-editor process-id _state-atom lang-kw question-attribute question-meta
           answers-atom mandatory? read-only? save! person-type]
    :as   args}]
  (let [persons (get @answers-atom question-attribute)
        process (session/get-entity process-id)
        person-schema (:data/template question-meta)
        wip-status? (-> process
                        :kyc-process/status
                        (= :kyc-process.status/wip))
        sorted-persons (sort-by :ord persons)
        person-missing-data-indices
        (->> sorted-persons
             (map-indexed
              (fn [idx person]
                (let [missing-fields (view-utils/resolve-missing-fields lang-kw
                                                                        person-schema
                                                                        (atom person)
                                                                        process)]
                  [idx missing-fields])))
             (filterv (fn [[_idx missing-fields]]
                        (seq missing-fields)))
             (mapv first)
             set)
        headers (get-table-headers person-type person-schema lang-kw)
        add-button-text (case person-type
                          :substantial-us-owner (loc/tr :add-substantial-us-owner nil lang-kw)
                          :controlling-person (loc/tr :add-controlling-person nil lang-kw)
                          (loc/tr :add-person nil lang-kw))

        mandatory? (and mandatory?
                        (editor-utils/mandatory?
                         question-attribute
                         question-meta
                         @answers-atom
                         process))]
    [:div
     [:div.input-editor__label
      [:label.input-editor__label-text.pb-2
       {:class
        (when (and mandatory?
                   (not (:hide-mandatory-asterix? question-meta)))
          "after:ml-0.5 after:text-danger-regular after:content-['*']")}
       (-> question-meta
           :translations
           (get lang-kw))]
      (when-let [info (-> question-meta
                          :info
                          (get lang-kw))]
        [:p
         {:class "text-sm m-0 font-medium text-base-light-text-paler mb-4"}
         info])]
     (if (and read-only?
              (empty? persons))
       [:div.preview
        [:span (case person-type
                 :substantial-us-owner (loc/tr :no-substantial-us-owners nil lang-kw)
                 :controlling-person (loc/tr :no-controlling-persons nil lang-kw)
                 (loc/tr :no-persons nil lang-kw))]]
       [:div.max-w-100.hide-scrollbar
        [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
         [:thead.zap-table__headline
          [:tr.zap-table__row
           (map-indexed (fn [idx header]
                          [:th.zap-table__headline {:key idx} header]) headers)]]
         [:tbody
          (->>
           sorted-persons
           (map-indexed
            (fn [idx person]
              (let [summary-fields (get-person-summary-fields person person-type person-schema lang-kw)]
                [:tr
                 {:key (str (name person-type) "-" idx "-" (:ord person))}
                 [:td.zap-table__cell
                  [:i.fal.fa-external-link.clickable
                   {:on-click (fn [_]
                                (open-person-editor (assoc args
                                                           :idx idx
                                                           :existing-person person
                                                           :person-type person-type)))}]]
                 (map-indexed (fn [field-idx [_field-key field-value]]
                                [:td.zap-table__cell {:key field-idx}
                                 (or field-value "")])
                              summary-fields)
                 [:td.zap-table__cell
                  (when (and wip-status?
                             (seq person-missing-data-indices))
                    {:style {:border-right "1px solid #D9D9DA"}})
                  (when-not read-only?
                    [:i.fal.fa-trash-alt.pointer
                     {:on-click (fn [_]
                                  (swap! answers-atom assoc
                                         question-attribute
                                         (concat (take idx persons)
                                                 (drop (inc idx) persons)))
                                  (save!))}])]

                 (when (and wip-status?
                            (contains? person-missing-data-indices idx))
                   [:td
                    {:class "px-3 py-2"}
                    [tooltip/view
                     [:i {:class "fal fa-exclamation-triangle text-danger-regular"}]
                     [:span (loc/tr :data-missing nil lang-kw)]]])])))
           (doall))]
         (when-not read-only?
           [:tfoot
            [:tr.zap-table__row
             [:td.zap-table__footer-cell {:col-span (count headers)}
              [button/primary-solid
               {:on-click (fn [_]
                            (let [idx (count persons)]
                              (open-person-editor (assoc args
                                                         :idx idx
                                                         :person-type person-type))))}
               [:i.fal.fa-plus.pr-2]
               add-button-text]]]])]])]))

;; Specific views for each type
(defn substantial-us-owners-view [args]
  (view (assoc args :person-type :substantial-us-owner)))

(defn controlling-persons-view [args]
  (view (assoc args :person-type :controlling-person)))
